import type { Currency } from '@inshuwa-client/models';

/** How money is labelled: `symbol` renders Kwacha as K, `code` renders it as ZMW. */
export type CurrencyDisplay = 'symbol' | 'code';

export const CURRENCY_DISPLAY_STORAGE_KEY = 'ic.currencyDisplay';

/** ZMW, which is what the platform showed before this setting existed. */
export const DEFAULT_CURRENCY_DISPLAY: CurrencyDisplay = 'code';

export function resolveCurrencyDisplay(
  storedRaw: string | null,
  fallback: CurrencyDisplay = DEFAULT_CURRENCY_DISPLAY,
): CurrencyDisplay {
  return storedRaw === 'symbol' || storedRaw === 'code' ? storedRaw : fallback;
}

interface FractionDigits {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

function formatterFor(
  currency: Currency | string,
  display: CurrencyDisplay,
  digits: FractionDigits,
): Intl.NumberFormat {
  // en-US has no symbol for ZMW and falls back to printing the code; en-ZM knows it.
  const locale = display === 'symbol' && currency === 'ZMW' ? 'en-ZM' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: display === 'code' ? 'code' : 'symbol',
    ...digits,
  });
}

/** Angular's currency pipe spells digits `'1.2-2'`; call sites moved over from it still pass that. */
function parseDigitsInfo(digitsInfo: string | undefined): FractionDigits {
  const match = digitsInfo && /^\d+\.(\d+)-(\d+)$/.exec(digitsInfo);
  if (!match) return {};
  return {
    minimumFractionDigits: Number(match[1]),
    maximumFractionDigits: Number(match[2]),
  };
}

// Building the formatter is the expensive half, and this runs at hundreds of call sites.
const formatters = new Map<string, Intl.NumberFormat>();

function cachedFormatter(
  currency: Currency | string,
  display: CurrencyDisplay,
  digitsInfo?: string,
): Intl.NumberFormat {
  const key = `${currency}:${display}:${digitsInfo ?? ''}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = formatterFor(currency, display, parseDigitsInfo(digitsInfo));
    formatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Tolerates whatever templates hold - numeric strings, nulls mid-load, codes with stray whitespace -
 * because these call sites came from Angular's currency pipe, which tolerated all of it.
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currency: Currency | string | null | undefined,
  display: CurrencyDisplay,
  digitsInfo?: string,
): string {
  const amount = typeof value === 'string' ? Number(value) : value;
  if (amount == null || !Number.isFinite(amount)) return '';

  try {
    return cachedFormatter(normalizeCurrency(currency), display, digitsInfo).format(amount);
  } catch {
    return amount.toLocaleString();
  }
}

function normalizeCurrency(currency: Currency | string | null | undefined): Currency {
  return (currency?.trim().toUpperCase() || 'ZMW') as Currency;
}

/** Just the label, for chart axes and tooltips that lay out their own abbreviated numbers. */
export function currencySymbol(currency: Currency, display: CurrencyDisplay): string {
  const part = cachedFormatter(currency, display)
    .formatToParts(0)
    .find((p) => p.type === 'currency');
  return part?.value ?? currency;
}
